import { PhoneMockup } from "./PhoneMockup";
import { Card } from "@/components/ui/card";
import { ImageIcon } from "lucide-react";
import { LightweightCarousel } from "./LightweightCarousel";

// All images use vite-imagetools for optimal sizing
import dashboardImg from "@/assets/screenshots/dashboard.jpg?format=webp&w=420";
import dailyRitualsImg from "@/assets/screenshots/daily-rituals.jpg?format=webp&w=420";
import readingCardImg from "@/assets/screenshots/reading-card.jpg?format=webp&w=420";
import badgesImg from "@/assets/screenshots/badges.jpg?format=webp&w=420";
import dailyChallengeImg from "@/assets/screenshots/daily-challenge.jpg?format=webp&w=420";
import horoscopeImg from "@/assets/screenshots/horoscope.jpg?format=webp&w=420";
import reflectionPromptsImg from "@/assets/screenshots/reflection-prompts.jpg?format=webp&w=420";

interface ScreenshotItem {
  id: string;
  src: string;
  alt: string;
  caption: string;
  description: string;
}

const screenshots: ScreenshotItem[] = [
  {
    id: "dashboard",
    src: dashboardImg,
    alt: "UnravelYou dashboard showing daily progress and mission overview",
    caption: "Your Personal Dashboard",
    description: "Track your daily progress, see your current streak, view Spirit Shards balance, and access your next mission. Your personalized home base for your 365-day journey.",
  },
  {
    id: "daily-rituals",
    src: dailyRitualsImg,
    alt: "Daily rituals page with tarot, horoscope, and affirmations",
    caption: "Daily Rituals",
    description: "Unlock daily tarot, personalized horoscopes, affirmations, mindfulness, and quotes using Spirit Shards. All FREE to unlock through your daily activities!",
  },
  {
    id: "reading-card",
    src: readingCardImg,
    alt: "Personalized reading result with spiritual insights",
    caption: "50 Personalized Readings",
    description: "Just $1 each — or earn them FREE with Spirit Shards. Every reading is crafted using your birth data, numerology, and cosmic profile. Download, share, or save.",
  },
  {
    id: "badges",
    src: badgesImg,
    alt: "Badge collection showing achievement progress",
    caption: "71 Collectible Badges",
    description: "Earn 21 achievement badges + 50 section badges as you complete readings and challenges. Celebrate milestones and share your achievements with friends.",
  },
  {
    id: "daily-challenge",
    src: dailyChallengeImg,
    alt: "Daily challenge completion screen",
    caption: "Daily Challenges",
    description: "Complete 3-6 wellness activities daily. Earn Spirit Shards for every challenge — exchange 10 Shards for 1 Credit to unlock premium readings for free!",
  },
  {
    id: "horoscope",
    src: horoscopeImg,
    alt: "Personalized horoscope view",
    caption: "Daily Horoscope",
    description: "Get your daily horoscope personalized to your zodiac sign AND life path number—a unique combination you won't find anywhere else.",
  },
  {
    id: "reflection-prompts",
    src: reflectionPromptsImg,
    alt: "Reflection prompts for journaling and self-discovery",
    caption: "Reflection Prompts",
    description: "Daily journaling prompts tied to your mission progress. Reflect on meaningful questions about love, purpose, growth, and more. Save your thoughts and track your inner journey.",
  },
];

const PlaceholderImage = ({ caption }: { caption: string }) => (
  <div className="w-full h-full bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20 flex flex-col items-center justify-center p-4">
    <ImageIcon className="w-12 h-12 text-white/40 mb-3" />
    <span className="text-white/60 text-xs text-center font-medium">{caption}</span>
    <span className="text-white/40 text-[10px] mt-1">Screenshot coming soon</span>
  </div>
);

export const ScreenshotGallery = () => {
  return (
    <section className="mx-auto px-0 md:px-4 pt-0 pb-20">
      {/* Carousel - Full width mobile, big on desktop */}
      <div className="w-full md:max-w-5xl lg:max-w-6xl mx-auto px-0 md:px-12 lg:px-16">
        <LightweightCarousel className="w-full">
          {screenshots.map((screenshot, index) => (
            <div key={screenshot.id} className="p-0 md:p-4">
              <Card className="bg-white/5 border-white/10 overflow-hidden mx-2 md:mx-0">
                <div className="flex flex-col items-center pt-0 pb-4 px-4 md:pt-0 md:pb-8 md:px-8">
                  {/* Phone Mockup - Bigger at all breakpoints */}
                  <PhoneMockup className="w-64 md:w-80 lg:w-96 transform hover:scale-105 transition-transform duration-300">
                    <img 
                      src={screenshot.src} 
                      alt={screenshot.alt}
                      width={375}
                      height={667}
                      className="w-full h-full object-contain object-top bg-gray-900"
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      {...(index === 0 ? { fetchpriority: 'high' as const } : {})}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden w-full h-full">
                      <PlaceholderImage caption={screenshot.caption} />
                    </div>
                  </PhoneMockup>
                  
                  {/* Combined Caption & Description */}
                  <div className="text-center mt-6 max-w-2xl px-4">
                    <p className="text-white text-base md:text-lg leading-relaxed">
                      <span className="font-bold">{screenshot.caption}:</span> {screenshot.description}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </LightweightCarousel>
      </div>
    </section>
  );
};
